<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { useGlobalState } from '@/composables/useGlobalState';
import { useToast } from '@/composables/useToast';
import ConfirmModal from './Utils/ConfirmModal.vue';

const { addToast } = useToast();
const { getAuthHeader } = useGlobalState();

const shares = ref<any[]>([]);
const newEmail = ref('');
const isSubmitting = ref(false);
const isLoading = ref(true);

const showConfirmModal = ref(false);
const shareToRemove = ref<number | null>(null);

const loadShares = async () => {
    try {
        const res = await fetch('/api/sport/shares', { headers: getAuthHeader() });
        shares.value = await res.json();
    } catch (e) {
        addToast('Erreur lors du chargement des partages', 'error');
    } finally {
        isLoading.value = false;
    }
};

const addShare = async () => {
    if (!newEmail.value) return;
    
    isSubmitting.value = true;
    try {
        const res = await fetch('/api/sport/shares', {
            method: 'POST',
            headers: {
                ...getAuthHeader(),
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ email: newEmail.value })
        });
        
        const data = await res.json();
        
        if (!res.ok) {
            addToast(data.error || 'Erreur lors de l\'ajout', 'error');
        } else {
            addToast('Accès accordé avec succès', 'success');
            newEmail.value = '';
            shares.value.push(data);
        }
    } catch (e) {
        addToast('Erreur serveur', 'error');
    } finally {
        isSubmitting.value = false;
    }
};

const promptRemoveShare = (id: number) => {
    shareToRemove.value = id;
    showConfirmModal.value = true;
};

const executeRemoveShare = async () => {
    if (!shareToRemove.value) return;
    
    const id = shareToRemove.value;
    showConfirmModal.value = false;
    shareToRemove.value = null;
    
    try {
        const res = await fetch(`/api/sport/shares/${id}`, {
            method: 'DELETE',
            headers: getAuthHeader()
        });
        
        if (res.ok) {
            shares.value = shares.value.filter(s => s.id !== id);
            addToast('Accès révoqué', 'success');
        } else {
            addToast('Erreur lors de la suppression', 'error');
        }
    } catch (e) {
        addToast('Erreur serveur', 'error');
    }
};

const formatDate = (iso: string) => {
    return new Date(iso).toLocaleDateString('fr-FR');
};

onMounted(loadShares);
</script>

<template>
    <div>
        <div class="row">
            <div class="col-md-6 mb-4">
                <div class="card border-0 shadow-sm rounded-4 h-100">
                    <div class="card-body p-4">
                        <h5 class="fw-bold mb-3">
                            <i class="fas fa-user-plus text-primary me-2"></i> Ajouter un accès
                        </h5>
                        <p class="text-muted small mb-4">
                            Saisissez l'adresse email d'un utilisateur Codifyo pour lui autoriser l'accès à votre calendrier sportif (séances à venir).
                        </p>
                        <form @submit.prevent="addShare">
                            <div class="mb-3">
                                <label class="form-label">Email de l'utilisateur</label>
                                <input v-model="newEmail" type="email" class="form-control bg-light border-0" placeholder="ami@example.com" required>
                            </div>
                            <button type="submit" class="btn btn-dark w-100 rounded-pill" :disabled="isSubmitting">
                                <i class="fas" :class="isSubmitting ? 'fa-spinner fa-spin' : 'fa-check'"></i> Autoriser l'accès
                            </button>
                        </form>
                    </div>
                </div>
            </div>
            
            <div class="col-md-6 mb-4">
                <div class="card border-0 shadow-sm rounded-4 h-100">
                    <div class="card-body p-4">
                        <h5 class="fw-bold mb-4">
                            <i class="fas fa-users text-primary me-2"></i> Utilisateurs autorisés
                        </h5>
                        
                        <div v-if="isLoading" class="text-center py-4">
                            <i class="fas fa-spinner fa-spin text-muted"></i>
                        </div>
                        <div v-else-if="shares.length === 0" class="text-center py-4 text-muted small">
                            Vous ne partagez votre calendrier avec personne.
                        </div>
                        <ul v-else class="list-group list-group-flush">
                            <li v-for="share in shares" :key="share.id" class="list-group-item d-flex justify-content-between align-items-center px-0 py-3 border-light">
                                <div class="d-flex align-items-center gap-3">
                                    <div class="avatar bg-light text-dark rounded-circle d-flex align-items-center justify-content-center" style="width: 40px; height: 40px; font-weight: bold;">
                                        {{ share.viewerName.charAt(0).toUpperCase() }}
                                    </div>
                                    <div>
                                        <div class="fw-bold">{{ share.viewerName }}</div>
                                        <div class="text-muted" style="font-size: 0.75rem;">
                                            Depuis le {{ formatDate(share.createdAt) }}
                                        </div>
                                    </div>
                                </div>
                                <button @click="promptRemoveShare(share.id)" class="btn btn-outline-danger btn-sm rounded-circle d-flex align-items-center justify-content-center" title="Révoquer l'accès" style="width: 32px; height: 32px; padding: 0;">
                                    <i class="fas fa-times"></i>
                                </button>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>

        <ConfirmModal 
            :is-open="showConfirmModal"
            title="Révoquer l'accès"
            message="Êtes-vous sûr de vouloir révoquer l'accès de cet utilisateur à votre calendrier ?"
            confirm-text="Révoquer"
            @close="showConfirmModal = false; shareToRemove = null"
            @confirm="executeRemoveShare"
        />
    </div>
</template>

<style scoped>
.form-control:focus {
    box-shadow: none;
    border-bottom: 2px solid #212529 !important;
}
</style>
